import { useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { type PatchedCustomerTagList, type Tag } from '@hungryroot/rest';
import {
  useTagGroup,
  useCustomerTagGroup,
  useCustomerTags,
  tags as TagEnum,
  tagGroups,
  useTag,
} from '@hungryroot/tags';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import {
  type TagDataLoader,
  type CustomerPreferenceLoader,
  type TagLoaderComponent,
} from '../types';
import { getShouldRenderTag } from '../utils';

/**
 * Gets tags for nutrition tag group and adds low sodium hard constraint tag
 */
export const useTagsData: TagDataLoader = (tagGroupId) => {
  const tagGroup = useTagGroup(tagGroupId);
  const lowSodiumHardRestraint = useTag(TagEnum.OnlyLowSodium)?.data;
  const { data: tagGroupData } = tagGroup || {};
  const queryTags = tagGroupData?.tags;

  if (lowSodiumHardRestraint && queryTags) {
    return [...queryTags, lowSodiumHardRestraint];
  }

  return queryTags || [];
};

/**
 * Loads customer tags from nutrition tag group, and also hard constraint tag for low sodium.
 */
export const useCustomerPreferenceLoader: CustomerPreferenceLoader = ({
  customerId,
  setCustomerTags,
}) => {
  const { data: customerTagGroupData, status: customerTagGroupStatus } =
    useCustomerTagGroup(tagGroups.nutrition);

  const {
    data: sodiumHardRestraintTagData,
    status: sodiumHardRestraintTagStatus,
  } = useCustomerTags([TagEnum.OnlyLowSodium]);

  useEffect(() => {
    if (
      customerTagGroupStatus !== 'success' ||
      customerTagGroupData === undefined ||
      sodiumHardRestraintTagStatus !== 'success'
    ) {
      return;
    }

    const prefs = customerTagGroupData.results.map((obj) => {
      return {
        customer_id: customerId,
        preference: obj.preference,
        tag: obj.tag,
      };
    });

    const hardConstraintTag = sodiumHardRestraintTagData?.results?.[0];

    const hardConstraintTagData = hardConstraintTag
      ? [
          {
            customer_id: customerId,
            preference: hardConstraintTag.preference,
            tag: hardConstraintTag.tag,
          },
        ]
      : [];

    setCustomerTags([...prefs, ...hardConstraintTagData]);
  }, [
    customerTagGroupData,
    customerTagGroupStatus,
    sodiumHardRestraintTagData,
    sodiumHardRestraintTagStatus,
    customerId,
    setCustomerTags,
  ]);
};

function sortNutritionTags(allTags: Tag[], isDesktop: boolean): Tag[] {
  if (!allTags.find((tag) => tag.id === TagEnum.OnlyLowSodium)) return allTags;

  const sortedTags = allTags.filter(
    (tag) => tag.id !== TagEnum.LowSodium && tag.id !== TagEnum.OnlyLowSodium
  );
  const lowSodiumTags = allTags.filter(
    (tag) => tag.id === TagEnum.LowSodium || tag.id === TagEnum.OnlyLowSodium
  );

  const lowSodiumIndex = isDesktop ? sortedTags.length - 1 : sortedTags.length;

  sortedTags.splice(lowSodiumIndex, 0, lowSodiumTags[0]);
  sortedTags.push(lowSodiumTags[1]);
  return sortedTags;
}

export const LowSodiumHardConstrainTagLoader: TagLoaderComponent = (props) => {
  const { buildSelectedMap, children } = props;
  const baseTags = useTagsData(tagGroups.nutrition);
  const isDesktop = useMedia(BreakpointQueryLarge);

  const [customerTags, setCustomerTags] = useState<PatchedCustomerTagList[]>(
    []
  );

  const selectedMap = buildSelectedMap({ customerTags, queryTags: baseTags });

  const tags = sortNutritionTags(
    baseTags.filter(getShouldRenderTag(tagGroups.nutrition)),
    isDesktop
  )
    .filter((tag) => {
      if (tag.id !== TagEnum.OnlyLowSodium) return true;
      return Boolean(selectedMap?.[TagEnum.LowSodium]);
    })
    .filter(Boolean);

  useCustomerPreferenceLoader({ ...props, setCustomerTags });
  return children({ tags, selectedMap, customerTags });
};
